/**
 * Shared scenario-tag pill renderer for create flow and rankings filters.
 * @typedef {{ id: number, label: string, tooltip?: string }} ScenarioTagOption
 */

/**
 * @param {HTMLElement} picker
 * @returns {number[]}
 */
const readSelectedIdsFromPicker = (picker) => {
  const ids = [];
  picker.querySelectorAll('.scenario-tag-pill.is-selected, .scenario-tag-pill[aria-pressed="true"]').forEach((btn) => {
    const id = Number(btn.dataset.tagId);
    if (Number.isFinite(id)) ids.push(id);
  });
  return normalizeTagIds(ids);
};

/**
 * @param {HTMLElement} picker
 * @param {Object} options
 * @returns {number[]}
 */
const getLiveSelectedIds = (picker, options) => {
  if (typeof options.getSelectedIds === 'function') {
    return normalizeTagIds(options.getSelectedIds());
  }
  return readSelectedIdsFromPicker(picker);
};

/**
 * @param {HTMLElement} picker
 * @param {ScenarioTagOption[]} tagOptions
 * @param {Object} options
 * @param {Array<number|string>} [options.selectedIds]
 * @param {() => Array<number|string>} [options.getSelectedIds]
 * @param {number} [options.maxTags]
 * @param {(nextIds: number[]) => void} [options.onChange]
 */
export const renderScenarioTagPills = (picker, tagOptions, options = {}) => {
  if (!picker) return;

  const maxTags = Number.isFinite(options.maxTags) ? options.maxTags : Infinity;
  const selectedIds = normalizeTagIds(options.selectedIds);
  const selectedSet = new Set(selectedIds);
  const atMax = selectedIds.length >= maxTags;

  picker.innerHTML = '';
  picker.classList.add('scenario-tags-picker');

  tagOptions.forEach((tag) => {
    const id = Number(tag.id);
    if (!Number.isFinite(id)) return;
    const label = tag.label || String(id);
    const isSelected = selectedSet.has(id);
    const shouldDim = atMax && !isSelected;

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'scenario-tag-pill';
    if (isSelected) btn.classList.add('is-selected');
    if (shouldDim) btn.classList.add('is-disabled');
    btn.dataset.tagId = String(id);
    btn.dataset.tagValue = label;
    btn.textContent = label;
    btn.setAttribute('aria-pressed', String(isSelected));
    btn.setAttribute('tabindex', '0');
    if (tag.tooltip) {
      btn.setAttribute('title', tag.tooltip);
      btn.setAttribute('aria-label', `${label}: ${tag.tooltip}`);
    } else {
      btn.setAttribute('aria-label', label);
    }

    const handleToggle = () => {
      if (typeof options.onChange !== 'function') return;
      const current = getLiveSelectedIds(picker, options);
      let next;
      if (current.includes(id)) {
        next = current.filter((t) => t !== id);
      } else {
        if (current.length >= maxTags) return;
        next = [...current, id];
      }
      options.onChange(next);
    };

    btn.addEventListener('click', handleToggle);
    btn.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleToggle();
      }
    });

    picker.appendChild(btn);
  });
};

/**
 * @param {HTMLElement} picker
 * @param {Array<number|string>} selectedIds
 * @param {number} [maxTags]
 */
export const syncScenarioTagPillState = (picker, selectedIds, maxTags = Infinity) => {
  if (!picker) return;
  const selected = normalizeTagIds(selectedIds);
  const selectedSet = new Set(selected);
  const atMax = selected.length >= maxTags;

  picker.querySelectorAll('.scenario-tag-pill').forEach((btn) => {
    const id = Number(btn.dataset.tagId);
    const isSelected = selectedSet.has(id);
    const shouldDim = atMax && !isSelected;
    btn.classList.toggle('is-selected', isSelected);
    btn.setAttribute('aria-pressed', String(isSelected));
    btn.classList.toggle('is-disabled', shouldDim);
    btn.disabled = false;
  });
};

/**
 * @param {unknown} value
 * @returns {number[]}
 */
export const normalizeTagIds = (value) => {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  const ids = [];
  value.forEach((item) => {
    const id = Number(item);
    if (!Number.isFinite(id) || seen.has(id)) return;
    seen.add(id);
    ids.push(id);
  });
  return ids;
};
